import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCollections } from "@/context/CollectionsContext";
import { LOGO } from "@/lib/api";

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const collections = useCollections();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const inputRef = useRef(null);

  const navLinks = [
    ...collections
      .filter((c) => c.handle !== "halloween")
      .map((c) => ({ label: c.title, to: `/shop/${c.handle}` })),
    { label: "Shop All", to: "/shop/all" },
    { label: "Halloween", to: "/halloween", special: true },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && inputRef.current) inputRef.current.focus();
  }, [searchOpen]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = term.trim();
    if (!q) return;
    setSearchOpen(false);
    setMenuOpen(false);
    setTerm("");
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${scrolled ? "bg-ink/95 backdrop-blur-xl" : "bg-ink"}`}
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between h-20 px-4 md:px-8 gap-4">
        <div className="flex items-center gap-6 lg:gap-8 min-w-0">
          <button className="md:hidden text-cream" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-open">
            <Menu size={24} />
          </button>

          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
            <img src={LOGO} alt="Rock Hard Tennis" className="h-12 w-12 object-contain" />
            <span className="hidden sm:block text-cream font-display text-xl leading-none tracking-wide whitespace-nowrap">ROCK HARD</span>
          </Link>

          <nav className="hidden md:flex gap-5 lg:gap-6 min-w-0 overflow-x-auto no-scrollbar text-cream text-xs font-bold uppercase tracking-[0.14em]">
            {navLinks.map((n) => (
              <Link key={n.to} to={n.to} className={`hover:text-sale transition-colors whitespace-nowrap ${n.special ? "text-orange-400 hover:text-orange-300" : ""}`} data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`}>
                {n.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-5 justify-end text-cream shrink-0">
          <button onClick={() => setSearchOpen((v) => !v)} className="hover:text-sale transition-colors" data-testid="search-toggle" aria-label="Search">
            <Search size={21} />
          </button>
          {user ? (
            <div className="hidden md:flex items-center gap-4 text-xs font-bold uppercase tracking-widest">
              <Link to="/orders" className="hover:text-sale transition-colors" data-testid="nav-orders">Orders</Link>
              <button onClick={() => { logout(); navigate("/"); }} className="hover:text-sale transition-colors" data-testid="nav-logout">Logout</button>
            </div>
          ) : (
            <Link to="/login" className="hover:text-sale transition-colors" data-testid="nav-account">
              <User size={22} />
            </Link>
          )}
          <button onClick={() => setOpen(true)} className="relative hover:text-sale transition-colors" data-testid="cart-toggle">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-sale text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="bg-ink border-t border-cream/10 px-4 md:px-8 py-4" data-testid="search-bar">
          <form onSubmit={submitSearch} className="max-w-[1440px] mx-auto flex items-center gap-3 border-b border-cream/30 pb-2">
            <Search size={18} className="text-cream/60" />
            <input
              ref={inputRef}
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search for gear..."
              className="flex-1 bg-transparent text-cream text-base focus:outline-none placeholder:text-cream/40"
              data-testid="search-input"
            />
            <button type="submit" className="text-cream text-xs font-bold uppercase tracking-widest hover:text-sale" data-testid="search-submit">Search</button>
            <button type="button" onClick={() => setSearchOpen(false)} className="text-cream/60 hover:text-cream"><X size={18} /></button>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-ink z-50 p-6 md:hidden" data-testid="mobile-menu">
          <div className="flex justify-between items-center mb-12">
            <span className="text-cream font-display text-2xl">ROCK HARD</span>
            <button onClick={() => setMenuOpen(false)} className="text-cream" data-testid="mobile-menu-close"><X size={28} /></button>
          </div>
          <nav className="flex flex-col gap-6 text-cream text-2xl font-heading font-bold uppercase">
            {navLinks.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} className={n.special ? "text-orange-400" : ""}>{n.label}</Link>
            ))}
            {user ? (
              <>
                <Link to="/orders" onClick={() => setMenuOpen(false)}>Orders</Link>
                <button className="text-left" onClick={() => { logout(); setMenuOpen(false); navigate("/"); }}>Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
